import { Bar, Box, Slab } from '../../render/parts'
import { Stool } from '../parts/seating'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Acoustic phone booth: four corner posts, three solid shell walls, a glazed
 * front and a flat roof banded with the accent colour.
 *
 * Layering rule used throughout: whatever is widest defines the silhouette and
 * everything behind it is stepped back by at least 3 mm, so the pure-white
 * surfaces never end up coplanar. Order outwards is
 * wall -> post -> roof -> accent band.
 *
 * The front is fully glazed rather than boarded up: at 22 % opacity the pane
 * lets the shelf and stool read from the isometric camera, which is what makes
 * the object legible as a booth instead of a white box.
 */

const DEPTH = 1.1
const HEIGHT = 2.25
const SINGLE_WIDTH = 1.1
const DUO_WIDTH = 1.6
const POST = 0.055
/** Underside of the roof slab. */
const ROOF_BASE = 2.18
/** Top of the walls and of the front frame — both die into the roof. */
const SHELL_TOP = 2.19
/** Bottom of the walls and of the front frame — both die into the floor pan. */
const SHELL_BASE = 0.02
const SHELF_TOP = 0.75

const CORNERS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
]

interface GlassFrontProps {
  width: number
  height: number
  /** One leaf, or two with a central mullion on the wider booth. */
  panes?: 1 | 2
}

/** Slim door frame with its own glazing. Centred on its own origin. */
function GlassFront({ width, height, panes = 1 }: GlassFrontProps) {
  const member = 0.032
  const depth = 0.03
  const inner = width - member * 2
  const mullion = member * 0.75
  const pane = panes === 2 ? (inner - mullion) / 2 : inner
  const stileX = width / 2 - member / 2 - 0.003
  const handleX = panes === 2 ? mullion / 2 + 0.008 : -stileX

  return (
    <group>
      {/* head and threshold carry the full width */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metalDark"
          size={[width, member, depth]}
          position={[0, side * (height / 2 - member / 2), 0]}
        />
      ))}

      {/* stiles, stepped inside the rails in both x and z */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metalDark"
          size={[member, height - member * 2 + 0.012, depth - 0.006]}
          position={[side * stileX, 0, 0]}
        />
      ))}

      {panes === 2 ? (
        <Box tone="metalDark" size={[mullion, height - member * 2 + 0.012, depth - 0.006]} />
      ) : null}

      {(panes === 2 ? [-1, 1] : [0]).map((side) => (
        <Box
          key={side}
          tone="glass"
          size={[pane + 0.012, height - member * 2 + 0.014, 0.01]}
          position={[side * (mullion / 2 + pane / 2), 0, 0]}
        />
      ))}

      {/* pull handle, sunk into the leading stile */}
      <Bar tone="dark" r={0.012} from={[handleX, -0.13, 0.02]} to={[handleX, 0.13, 0.02]} />
    </group>
  )
}

/** Cantilevered work shelf with a front apron. */
function BoothShelf({ width, z }: { width: number; z: number }) {
  return (
    <group>
      <Slab
        tone="shell"
        size={[width, 0.35, 0.03]}
        radius={0.008}
        position={[0, SHELF_TOP - 0.015, z]}
      />
      <Box
        tone="shellSoft"
        size={[width - 0.03, 0.054, 0.016]}
        position={[0, SHELF_TOP - 0.051, z + 0.161]}
      />
    </group>
  )
}

function PhoneBooth({ accent, variant = 'single' }: AssetContext) {
  const duo = variant === 'duo'
  const width = duo ? DUO_WIDTH : SINGLE_WIDTH
  const postX = width / 2 - 0.008 - POST / 2
  const postZ = DEPTH / 2 - 0.008 - POST / 2
  const shelfWidth = duo ? 0.9 : 0.5
  // Back edge of the shelf is buried 6 mm into the back wall.
  const shelfZ = -(DEPTH / 2 - 0.048) + 0.175
  const shellHeight = SHELL_TOP - SHELL_BASE
  const shellY = (SHELL_TOP + SHELL_BASE) / 2
  const stools = duo ? [-0.34, 0.34] : [0]

  return (
    <group>
      {/* raised floor pan — the walls and the door threshold all land on it */}
      <Slab
        tone="shellSoft"
        size={[width - 0.024, DEPTH - 0.024, 0.032]}
        radius={0.012}
        position={[0, 0.016, 0]}
      />

      {/* corner posts, 8 mm inside the roof edge */}
      {CORNERS.map(([sx, sz]) => (
        <Box
          key={`${sx},${sz}`}
          tone="metalDark"
          size={[POST, 2.2, POST]}
          position={[sx * postX, 1.1, sz * postZ]}
        />
      ))}

      {/* side walls, recessed behind the posts */}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="shell"
          size={[0.04, shellHeight, DEPTH - 0.06]}
          position={[side * (width / 2 - 0.034), shellY, 0]}
        />
      ))}

      {/* back wall */}
      <Box
        tone="shell"
        size={[width - 0.06, shellHeight, 0.04]}
        position={[0, shellY, -(DEPTH / 2 - 0.034)]}
      />

      {/* flat roof, with the accent band protruding to the exact footprint */}
      <Slab
        tone="shell"
        size={[width - 0.008, DEPTH - 0.008, HEIGHT - ROOF_BASE]}
        radius={0.014}
        position={[0, (ROOF_BASE + HEIGHT) / 2, 0]}
      />
      <Slab
        tone="accent"
        color={accent}
        size={[width, DEPTH, 0.016]}
        radius={0.014}
        position={[0, 2.196, 0]}
      />

      {/* glazed front, set back behind the front posts */}
      <group position={[0, shellY, DEPTH / 2 - 0.042]}>
        <GlassFront width={width - 0.1} height={shellHeight} panes={duo ? 2 : 1} />
      </group>

      <BoothShelf width={shelfWidth} z={shelfZ} />

      {stools.map((x) => (
        <group key={x} position={[x, 0.028, 0.05]}>
          <Stool height={0.62} />
        </group>
      ))}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'phone-booth',
  name: 'Cabina telefónica',
  category: 'partitions',
  // Declared at the wider variant; the single booth is centred inside it.
  footprint: [DUO_WIDTH, DEPTH],
  height: HEIGHT,
  seats: 1,
  occupantSide: 'around',
  accentable: true,
  variants: [
    { id: 'single', name: 'Individual', footprint: [SINGLE_WIDTH, DEPTH] },
    { id: 'duo', name: 'Dos personas', footprint: [DUO_WIDTH, DEPTH] },
  ],
  keywords: [
    'cabina',
    'telefonica',
    'booth',
    'phone booth',
    'llamadas',
    'acustica',
    'focus room',
    'privacidad',
  ],
  Component: PhoneBooth,
}
